import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  FaCalendarCheck, 
  FaLocationArrow,
  FaMobile,
  FaChartBar,
  FaRoute,
  FaDollarSign,
  FaClock,
  FaBell,
  FaMapMarkedAlt,
  FaClipboardCheck,
  FaUserClock,
  FaFileSignature,
  FaChartLine,
  FaArrowRight,
  FaPhoneAlt,
  FaCalculator,
  FaEnvelope,
  FaHandshake,
  FaSearch,
  FaBrain,
  FaChartPie,
  FaGasPump,
} from 'react-icons/fa';
import TrackingDemo from './TrackingDemo';
import EstimateDemo from './EstimateDemo';
import ChartDemo from './ChartDemo';
import SchedulingDemo from './SchedulingDemo';

const FeaturesSection = styled.section`
  background: ${props => props.theme.colors.background || '#ffffff'};
  overflow: hidden;
  position: relative;
`;

const SchedulingContainer = styled.div`
  min-height: 95vh;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4rem;
  padding: 4rem 2rem;
  position: relative;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    min-height: auto;
    gap: 1rem;
    padding: 2rem 1rem;
  }
`;

const TrackingContainer = styled(SchedulingContainer)`
  min-height: 100vh;
`;

const EstimatesContainer = styled(SchedulingContainer)`
  min-height: 105vh;
`;

const AnalyticsContainer = styled(SchedulingContainer)`
  min-height: 100vh;
  margin-bottom: 2rem;
`;

const SubTitle = styled.div`
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${props => props.color || props.theme.colors.primary || '#4f46e5'};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease-out;

  .in-view & {
    opacity: 1;
    transform: translateY(0);
  }

  svg {
    font-size: 1.25rem;
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const TextContent = styled.div`
  width: 35%;
  padding: 0.5rem;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 1024px) {
    width: 100%;
    text-align: center;
    padding: 0;
    margin-bottom: 0.25rem;
  }
`;

const FeatureTitle = styled.h2`
  font-size: 2.75rem;
  margin-bottom: 1rem;
  opacity: 0;
  transform: translateY(30px);
  filter: blur(10px);
  transition: all 0.8s ease-out;
  color: ${props => props.theme.colors.text || '#000000'};
  line-height: 1.1;

  .in-view & {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.75rem;
    line-height: 1.2;
    transform: translateY(15px);
  }
`;

const FeatureDescription = styled.p`
  font-size: 1.15rem;
  line-height: 1.4;
  opacity: 0;
  transform: translateY(30px);
  filter: blur(10px);
  transition: all 0.8s ease-out;
  transition-delay: 0.2s;
  color: ${props => props.theme.colors.text || '#000000'};
  margin: 0;
  max-width: 42ch;

  .in-view & {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.4;
    max-width: 90%;
    margin: 0 auto;
    transform: translateY(15px);
  }
`;

const VisualContent = styled.div`
  width: 50%;
  height: 630px;
  position: relative;
  z-index: 1;
  max-width: 650px;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    height: 500px;
    min-height: ${props => props.type === 'estimate' ? '500px' : '500px'};
  }

  @media (max-width: 768px) {
    height: 400px;
    min-height: ${props => props.type === 'estimate' ? '400px' : '400px'};
  }
`;

const DemoCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 3rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  transform: translateY(50px) scale(0.95);
  transition: all 0.8s ease-out;
  transition-delay: 0.4s;

  .in-view & {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  @media (max-width: 1024px) {
    padding: 2rem;
    position: relative;
    height: 100%;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    height: 100%;
  }
`;

const BulletPoints = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease-out;
  transition-delay: 0.3s;

  .in-view & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BulletPoint = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  color: ${props => props.theme.colors.text || '#374151'};

  svg {
    color: ${props => props.theme.colors.primary || '#4f46e5'};
    flex-shrink: 0;
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    text-align: left;
    font-size: 0.95rem;
  }
`;

const FeatureButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.5rem;
  border-radius: 100px;
  background: ${props => props.theme.colors.primary || '#4f46e5'};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease-out;
  transition-delay: 0.4s;
  width: fit-content;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(79, 70, 229, 0.25);
    background: ${props => props.theme.colors.primaryDark || '#4338ca'};
  }

  .in-view & {
    opacity: 1;
    transform: translateY(0);
  }

  svg {
    font-size: 1rem;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  @media (max-width: 768px) {
    width: auto;
    margin: 1.5rem auto 0;
    font-size: 0.95rem;
    padding: 0.75rem 1.25rem;
  }
`;

const Features = () => {
  const featuresRef = useRef([]);
  const sectionRef = useRef(null);

  const features = [
    {
      subtitle: "Smarter Software",
      title: "Don't Skip a Beat, But Skip the Paperwork",
      description: "Ditch the clipboard and streamline your workflow with ServTrackr, enabling you to create estimates and invoices in the palm of your hand. Elevate customer care by seamlessly converting estimates into active projects, assigning employees, and managing Project Events effortlessly",
      bullets: [
        { text: "Create Projects From Estimates Seamlessly With One Click", icon: FaUserClock },
        { text: "Setup Employee Checklists Per Project", icon: FaDollarSign },
        { text: "Assign Employees and Manage Project Events", icon: FaBell }
      ],
      buttonText: "Learn About Scheduling",
      buttonLink: "/features/job-scheduling",
      icon: FaCalendarCheck,
      color: "#4f46e5",
      demo: 'scheduling'
    },
    {
      subtitle: "Team Management",
      title: "Accurate Time Tracking",
      description: "Modernize your time tracking with digital clock-ins that provide accurate data for billing and payroll. Our flexible system lets you easily manage and adjust time entries while maintaining precise records for your business.",
      bullets: [
        { text: "Eliminate Manual Errors in Recording Work Hours", icon: FaMapMarkedAlt },
        { text: "Streamline Payroll Processing", icon: FaClipboardCheck },
        { text: "Mobile App for On-the-Go Time Tracking", icon: FaMobile }
      ],
      buttonText: "Explore Team Tracking",
      buttonLink: "/features/dispatch",
      icon: FaLocationArrow,
      color: "#7c3aed",
      demo: 'tracking'
    },
    {
      subtitle: "Sales & Payments",
      title: "Smart Estimates",
      description: "Generate professional estimates instantly with our user friendly system that helps you manage every part, visit and service item you may have",
      bullets: [
        { text: "One-click estimate generation", icon: FaCalculator },
        { text: "Smart pricing recommendations", icon: FaDollarSign },
        { text: "Digital signature and approval", icon: FaFileSignature },
        { text: "Automated follow-ups and reminders", icon: FaEnvelope }
      ],
      buttonText: "View Estimate Features",
      buttonLink: "/features/invoicing",
      icon: FaHandshake,
      color: "#06b6d4",
      demo: 'estimate'
    },
    {
      subtitle: "Fleet Optimization",
      title: "Smart Route Planning",
      description: "Maximize efficiency with our intelligent route runner feature that optimizes your service routes based on real-time data. Reduce drive time, save on fuel costs, and complete more jobs per day with AI-powered route optimization.",
      bullets: [
        { text: "Map jobs to optimal routes based on traffic patterns", icon: FaRoute },
        { text: "Minimize fuel costs with mileage optimization", icon: FaGasPump || FaCalculator },
        { text: "Prioritize urgent jobs while maintaining efficient routes", icon: FaClock },
        { text: "Track fleet performance and identify improvement areas", icon: FaChartLine }
      ],
      buttonText: "Explore Route Runner",
      buttonLink: "/features/route-optimization",
      icon: FaRoute,
      color: "#0ea5e9",
      demo: 'routes'
    }
  ];

  useEffect(() => {
    const sections = featuresRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      {
        root: null,
        rootMargin: '-10%',
        threshold: 0.1
      }
    );

    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  const renderDemo = (type) => {
    switch (type) {
      case 'scheduling':
        return <SchedulingDemo />;
      case 'tracking':
        return <TrackingDemo />;
      case 'estimate':
        return <EstimateDemo />;
      case 'analytics':
        return <ChartDemo />;
      default:
        return null;
    }
  };

  return (
    <FeaturesSection ref={sectionRef}>
      <SchedulingContainer ref={el => featuresRef.current[0] = el}>
        <TextContent>
          <SubTitle color={features[0].color}>
            {React.createElement(features[0].icon)}
            {features[0].subtitle}
          </SubTitle>
          <FeatureTitle className="feature-title">
            {features[0].title}
          </FeatureTitle>
          <FeatureDescription className="feature-description">
            {features[0].description}
          </FeatureDescription>
          <BulletPoints>
            {features[0].bullets.map((bullet, index) => (
              <BulletPoint key={index}>
                {React.createElement(bullet.icon)}
                {bullet.text}
              </BulletPoint>
            ))}
          </BulletPoints>
          <FeatureButton href={features[0].buttonLink}>
            {features[0].buttonText}
            <FaArrowRight />
          </FeatureButton>
        </TextContent>
        <VisualContent type="scheduling">
          <DemoCard className="demo-card" type="scheduling">
            {renderDemo('scheduling')}
          </DemoCard>
        </VisualContent>
      </SchedulingContainer>

      <TrackingContainer ref={el => featuresRef.current[1] = el}>
        <TextContent>
          <SubTitle color={features[1].color}>
            {React.createElement(features[1].icon)}
            {features[1].subtitle}
          </SubTitle>
          <FeatureTitle className="feature-title">
            {features[1].title}
          </FeatureTitle>
          <FeatureDescription className="feature-description">
            {features[1].description}
          </FeatureDescription>
          <BulletPoints>
            {features[1].bullets.map((bullet, index) => (
              <BulletPoint key={index}>
                {React.createElement(bullet.icon)}
                {bullet.text}
              </BulletPoint>
            ))}
          </BulletPoints>
          <FeatureButton href={features[1].buttonLink}>
            {features[1].buttonText}
            <FaArrowRight />
          </FeatureButton>
        </TextContent>
        <VisualContent type="tracking">
          <DemoCard className="demo-card" type="tracking">
            {renderDemo('tracking')}
          </DemoCard>
        </VisualContent>
      </TrackingContainer>

      <EstimatesContainer ref={el => featuresRef.current[2] = el}>
        <TextContent>
          <SubTitle color={features[2].color}>
            {React.createElement(features[2].icon)}
            {features[2].subtitle}
          </SubTitle>
          <FeatureTitle className="feature-title">
            {features[2].title}
          </FeatureTitle>
          <FeatureDescription className="feature-description">
            {features[2].description}
          </FeatureDescription>
          <BulletPoints>
            {features[2].bullets.map((bullet, index) => (
              <BulletPoint key={index}>
                {React.createElement(bullet.icon)}
                {bullet.text}
              </BulletPoint>
            ))}
          </BulletPoints>
          <FeatureButton href={features[2].buttonLink}>
            {features[2].buttonText}
            <FaArrowRight />
          </FeatureButton>
        </TextContent>
        <VisualContent type="estimate">
          <DemoCard className="demo-card" type="estimate">
            {renderDemo('estimate')}
          </DemoCard>
        </VisualContent>
      </EstimatesContainer>

      <AnalyticsContainer ref={el => featuresRef.current[3] = el}>
        <TextContent>
          <SubTitle color={features[3].color}>
            {React.createElement(features[3].icon)}
            {features[3].subtitle}
          </SubTitle>
          <FeatureTitle className="feature-title">
            {features[3].title}
          </FeatureTitle>
          <FeatureDescription className="feature-description">
            {features[3].description}
          </FeatureDescription>
          <BulletPoints>
            {features[3].bullets.map((bullet, index) => (
              <BulletPoint key={index}>
                {React.createElement(bullet.icon)}
                {bullet.text}
              </BulletPoint>
            ))}
          </BulletPoints>
          <FeatureButton href={features[3].buttonLink}>
            {features[3].buttonText}
            <FaArrowRight />
          </FeatureButton>
        </TextContent>
        <VisualContent type="analytics">
          <DemoCard className="demo-card" type="analytics">
            {renderDemo('analytics')}
          </DemoCard>
        </VisualContent>
      </AnalyticsContainer>
    </FeaturesSection>
  );
};

export default Features;
